import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import {
  getAlertGlobalSettings,
  updateAlertGlobalSettings,
  sendTestTelegramAlert,
  validateTelegramChatId,
} from "@/lib/alert-global-settings.functions";
import { validateTelegramChatIdFormat } from "@/lib/telegram-chat-id";
import { listSmtpAccounts } from "@/lib/smtp-accounts.functions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/alert-global-settings")({
  component: AlertGlobalSettingsPage,
  head: () => ({ meta: [{ title: "Globale Alarm-Einstellungen — InboxPulse" }] }),
});

function AlertGlobalSettingsPage() {
  const fetchSettings = useServerFn(getAlertGlobalSettings);
  const saveSettings = useServerFn(updateAlertGlobalSettings);
  const sendTest = useServerFn(sendTestTelegramAlert);
  const validateChat = useServerFn(validateTelegramChatId);
  const fetchSmtp = useServerFn(listSmtpAccounts);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["alert-global-settings"],
    queryFn: () => fetchSettings(),
  });
  const { data: smtpData } = useQuery({
    queryKey: ["smtp-accounts-for-alerts"],
    queryFn: () => fetchSmtp(),
  });
  const storedChatId = data?.telegram_chat_id?.trim() ?? "";
  const effectiveChatId = storedChatId || data?.envFallbacks.telegram_chat_id?.trim() || "";
  const chatValidation = useQuery({
    queryKey: ["telegram-chat-validation", effectiveChatId],
    queryFn: () => validateChat({ data: { telegram_chat_id: effectiveChatId } }),
    enabled: effectiveChatId.length > 0,
    retry: false,
  });

  const [form, setForm] = useState({
    telegram_chat_id: "",
    score_drop_threshold: 15,
    score_below: 50,
    bounce_telegram_enabled: true,
    warmup_alert_enabled: true,
    warmup_alert_email: "",
    warmup_alert_smtp_account_id: "",
    warmup_slack_webhook_url: "",
    warmup_alert_email_subject: "",
    warmup_alert_email_body_html: "",
    warmup_alert_email_body_text: "",
    warmup_alert_slack_text: "",
    warmup_alert_telegram_text: "",
  });
  const trimmedInput = form.telegram_chat_id.trim();
  const formatCheck = trimmedInput ? validateTelegramChatIdFormat(trimmedInput) : null;
  const formatError = formatCheck && !formatCheck.ok ? formatCheck.reason : null;

  const [testMessage, setTestMessage] = useState("");

  useEffect(() => {
    if (data) {
      setForm({
        telegram_chat_id: data.telegram_chat_id ?? "",
        score_drop_threshold: data.score_drop_threshold,
        score_below: data.score_below,
        bounce_telegram_enabled: data.bounce_telegram_enabled ?? true,
        warmup_alert_enabled: data.warmup_alert_enabled ?? true,
        warmup_alert_email: data.warmup_alert_email ?? "",
        warmup_alert_smtp_account_id: data.warmup_alert_smtp_account_id ?? "",
        warmup_slack_webhook_url: data.warmup_slack_webhook_url ?? "",
        warmup_alert_email_subject: data.warmup_alert_email_subject ?? "",
        warmup_alert_email_body_html: data.warmup_alert_email_body_html ?? "",
        warmup_alert_email_body_text: data.warmup_alert_email_body_text ?? "",
        warmup_alert_slack_text: data.warmup_alert_slack_text ?? "",
        warmup_alert_telegram_text: data.warmup_alert_telegram_text ?? "",
      });
      setTestMessage(data.warmup_alert_telegram_text ?? "");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      saveSettings({
        data: {
          telegram_chat_id: form.telegram_chat_id.trim() || null,
          score_drop_threshold: form.score_drop_threshold,
          score_below: form.score_below,
          bounce_telegram_enabled: form.bounce_telegram_enabled,
          warmup_alert_enabled: form.warmup_alert_enabled,
          warmup_alert_email: form.warmup_alert_email.trim() || null,
          warmup_alert_smtp_account_id: form.warmup_alert_smtp_account_id || null,
          warmup_slack_webhook_url: form.warmup_slack_webhook_url.trim() || null,
          warmup_alert_email_subject: form.warmup_alert_email_subject.trim() || null,
          warmup_alert_email_body_html: form.warmup_alert_email_body_html || null,
          warmup_alert_email_body_text: form.warmup_alert_email_body_text || null,
          warmup_alert_slack_text: form.warmup_alert_slack_text || null,
          warmup_alert_telegram_text: form.warmup_alert_telegram_text || null,
        },
      }),
    onSuccess: () => {
      toast.success("Einstellungen gespeichert");
      qc.invalidateQueries({ queryKey: ["alert-global-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const test = useMutation({
    mutationFn: () => {
      const chatId = form.telegram_chat_id.trim() || effectiveChatId;
      if (!chatId) throw new Error("Keine Telegram-Chat-ID konfiguriert");
      return sendTest({
        data: {
          telegram_chat_id: chatId,
          message: testMessage.trim() || undefined,
        },
      });
    },
    onSuccess: () => toast.success("Test-Nachricht gesendet"),
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Lade…</div>;
  if (error) return <div className="p-8 text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const env = data.envFallbacks;

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <PageHeader
        title="Globale Alarm-Einstellungen"
        description="Empfänger und Schwellen für die globalen Telegram-Benachrichtigungen (Blacklist, Score-Drop, niedriger Score)."
      />

      <HelpPanel
        title="Hilfe: Standard-Empfänger für alle Warnungen"
        what={
          <>
            Hier trägst du <b>einmal</b> ein, wer bei Standard-Vorfällen (Blacklist-Eintrag,
            Reputations-Sturz, niedriger Health-Score) eine Telegram-Nachricht bekommt.
          </>
        }
        why={
          <>
            Spart Arbeit: statt jede Regel einzeln zu konfigurieren, gilt diese Einstellung für alle
            drei wichtigsten globalen Alarme.
          </>
        }
        how={[
          <>Telegram-Chat-ID(s) eintragen, getrennt mit Komma.</>,
          <>Schwellen anpassen (Standard ist meist ok).</>,
          <>
            <b>Speichern</b> — Änderung wirkt sofort.
          </>,
          <>
            <b>Test-Nachricht senden</b> klicken, um die Verbindung zu prüfen.
          </>,
        ]}
        tips={[<>Mindestens 2 Personen eintragen, falls einer im Urlaub ist.</>]}
        glossary={[
          {
            term: "Score-Drop",
            def: "Plötzliches Absinken des Health-Scores einer Domain/Absender.",
          },
        ]}
      />

      <Card className="p-6 bg-card/60 border-border space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="telegram_chat_id">Telegram-Chat-ID</Label>
            {env.telegram_chat_id && !form.telegram_chat_id && (
              <Badge
                variant="secondary"
                title={`Fallback: ALERT_TELEGRAM_CHAT_ID = ${env.telegram_chat_id}`}
              >
                env-Fallback aktiv
              </Badge>
            )}
          </div>
          <Input
            id="telegram_chat_id"
            inputMode="numeric"
            placeholder="z. B. 123456789 oder -1001234567890"
            value={form.telegram_chat_id}
            onChange={(e) => setForm((p) => ({ ...p, telegram_chat_id: e.target.value }))}
            aria-invalid={formatError ? true : undefined}
            className={
              formatError ? "border-destructive focus-visible:ring-destructive" : undefined
            }
          />
          {formatError && (
            <div
              className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
              role="alert"
            >
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <div>
                <p className="font-medium">Ungültige Telegram-Chat-ID</p>
                <p className="mt-0.5 text-xs">{formatError}</p>
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Numerische Chat-ID des Empfängers. Leer lassen, um den Wert aus dem Secret{" "}
            <code>ALERT_TELEGRAM_CHAT_ID</code> zu nutzen.
          </p>
          {effectiveChatId &&
            (!form.telegram_chat_id.trim() || form.telegram_chat_id.trim() === storedChatId) && (
              <div
                className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
                  chatValidation.data?.valid
                    ? "border-primary/30 bg-primary/5 text-foreground"
                    : "border-destructive/40 bg-destructive/10 text-destructive"
                }`}
                role={chatValidation.data?.valid ? "status" : "alert"}
              >
                {chatValidation.isPending ? (
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" />
                ) : chatValidation.data?.valid ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" />
                )}
                <div>
                  <p className="font-medium">
                    {chatValidation.isPending
                      ? "Gespeicherte Chat-ID wird geprüft…"
                      : chatValidation.data?.valid
                        ? "Telegram-Verbindung bestätigt"
                        : `Ungültige ${storedChatId ? "gespeicherte" : "aktive"} Telegram-Chat-ID`}
                  </p>
                  {!chatValidation.isPending && (
                    <p className="mt-0.5 text-xs">
                      {chatValidation.data?.valid && chatValidation.data.chatTitle
                        ? `${chatValidation.data.message}: ${chatValidation.data.chatTitle}`
                        : (chatValidation.data?.message ??
                          (chatValidation.error as Error | null)?.message ??
                          "Die Chat-ID konnte nicht geprüft werden.")}
                    </p>
                  )}
                </div>
              </div>
            )}
          {form.telegram_chat_id.trim() && form.telegram_chat_id.trim() !== storedChatId && (
            <p className="text-xs text-muted-foreground">
              Der geänderte Wert wird beim Speichern automatisch geprüft.
            </p>
          )}

          <div className="space-y-2 pt-1">
            <Label htmlFor="test_message">Test-Nachricht</Label>
            <Textarea
              id="test_message"
              placeholder="Nachricht, die beim Testen gesendet wird…"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              rows={3}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Leer lassen für die Standard-Testnachricht.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => test.mutate()}
              disabled={test.isPending || !effectiveChatId}
            >
              {test.isPending ? (
                <Loader2 className="mr-1.5 size-4 animate-spin" />
              ) : (
                <Send className="mr-1.5 size-4" />
              )}
              Telegram testen
            </Button>
            {test.isSuccess && (
              <span className="text-xs text-emerald-500 flex items-center gap-1">
                <CheckCircle2 className="size-3.5" />
                Nachricht erfolgreich gesendet
              </span>
            )}
            {test.isError && (
              <span className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="size-3.5" />
                {test.error?.message ?? "Fehler"}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="score_drop_threshold">Score-Drop-Schwelle</Label>
            <Input
              id="score_drop_threshold"
              type="number"
              min={1}
              max={100}
              value={form.score_drop_threshold}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  score_drop_threshold: Number.parseInt(e.target.value, 10) || 0,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Alarm bei Rückgang um ≥ X Punkte (1–100).
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="score_below">Score-Below-Schwelle</Label>
            <Input
              id="score_below"
              type="number"
              min={1}
              max={100}
              value={form.score_below}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  score_below: Number.parseInt(e.target.value, 10) || 0,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Alarm beim Übergang unter diesen Score (1–100).
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <Label htmlFor="bounce_tg" className="font-medium">
              Telegram-Bounce-Benachrichtigungen
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Globaler Schalter für Telegram-Nachrichten bei Bounces. Kann pro Kampagne
              überschrieben werden.
            </p>
          </div>
          <Switch
            id="bounce_tg"
            checked={form.bounce_telegram_enabled}
            onCheckedChange={(v) => setForm((p) => ({ ...p, bounce_telegram_enabled: v }))}
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Zuletzt aktualisiert: {new Date(data.updated_at).toLocaleString()}
          </p>
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !!formatError}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {save.isPending ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border space-y-5">
        <div>
          <h3 className="text-sm font-semibold">Warmup-Auto-Pause-Warnungen</h3>
          <p className="text-xs text-muted-foreground">
            Zusätzliche Benachrichtigungen (E-Mail, Slack) wenn die Warmup-Automatik ein SMTP-Konto
            wegen zu hoher Bounce-/Complaint-Rate automatisch pausiert. Telegram wird unabhängig
            davon an die oben eingetragene Chat-ID gesendet.
          </p>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <Label htmlFor="warmup_alert_enabled" className="font-medium">
              Warmup-Alarme aktiv
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Globaler Schalter für E-Mail- und Slack-Benachrichtigungen bei Auto-Pause.
            </p>
          </div>
          <Switch
            id="warmup_alert_enabled"
            checked={form.warmup_alert_enabled}
            onCheckedChange={(v) => setForm((p) => ({ ...p, warmup_alert_enabled: v }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="warmup_alert_email">Warn-E-Mail (Empfänger)</Label>
          <Input
            id="warmup_alert_email"
            type="email"
            placeholder="alerts@deinedomain.de"
            value={form.warmup_alert_email}
            onChange={(e) => setForm((p) => ({ ...p, warmup_alert_email: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Empfänger der Warnmail. Leer lassen, um nur Slack/Telegram zu nutzen.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="warmup_alert_smtp">SMTP-Konto für Warnmail</Label>
          <Select
            value={form.warmup_alert_smtp_account_id || "__auto__"}
            onValueChange={(v) =>
              setForm((p) => ({
                ...p,
                warmup_alert_smtp_account_id: v === "__auto__" ? "" : v,
              }))
            }
          >
            <SelectTrigger id="warmup_alert_smtp">
              <SelectValue placeholder="Automatisch (betroffenes Konto)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__auto__">Automatisch (betroffenes Konto)</SelectItem>
              {(smtpData?.accounts ?? []).map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.label} — {a.from_email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Über welches SMTP-Konto die Warnmail verschickt wird. "Automatisch" nutzt das pausierte
            Konto selbst (kann hilfreich sein, um SMTP-Probleme sofort zu erkennen).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="warmup_slack_webhook">Slack Incoming-Webhook-URL</Label>
          <Input
            id="warmup_slack_webhook"
            type="url"
            placeholder="https://hooks.slack.com/services/T.../B.../..."
            value={form.warmup_slack_webhook_url}
            onChange={(e) => setForm((p) => ({ ...p, warmup_slack_webhook_url: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Slack-Webhook URL (Slack App → Incoming Webhooks). Sendet eine Alarmnachricht mit
            Konto-ID, Grund und letzten 24h-Werten.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {save.isPending ? "Speichere…" : "Warmup-Alarme speichern"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border space-y-5">
        <div>
          <h3 className="text-sm font-semibold">Warmup-Auto-Pause: Nachrichten-Templates</h3>
          <p className="text-xs text-muted-foreground">
            Passe Betreff und Inhalt der Auto-Pause-Warnungen pro Kanal an. Felder leer lassen, um
            die Standardvorlage zu verwenden. Verfügbare Platzhalter:{" "}
            <code className="text-[10px]">
              {
                "{{account_label}} {{account_from}} {{account_id}} {{reason}} {{sent_24h}} {{bounced_24h}} {{complaints_24h}} {{bounce_rate_pct}} {{complaint_rate_pct}} {{current_limit}}"
              }
            </code>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tpl_email_subject">E-Mail Betreff</Label>
          <Input
            id="tpl_email_subject"
            placeholder="⚠️ Warmup automatisch pausiert: {{account_label}}"
            value={form.warmup_alert_email_subject}
            onChange={(e) => setForm((p) => ({ ...p, warmup_alert_email_subject: e.target.value }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tpl_email_html">E-Mail HTML-Body</Label>
          <Textarea
            id="tpl_email_html"
            rows={8}
            className="font-mono text-xs"
            placeholder="<h2>⚠️ Warmup pausiert</h2><p>Konto: {{account_label}} …</p>"
            value={form.warmup_alert_email_body_html}
            onChange={(e) =>
              setForm((p) => ({ ...p, warmup_alert_email_body_html: e.target.value }))
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tpl_email_text">E-Mail Text-Body (Plaintext-Fallback)</Label>
          <Textarea
            id="tpl_email_text"
            rows={6}
            className="font-mono text-xs"
            placeholder={"⚠️ Warmup pausiert\nKonto: {{account_label}}\nGrund: {{reason}}"}
            value={form.warmup_alert_email_body_text}
            onChange={(e) =>
              setForm((p) => ({ ...p, warmup_alert_email_body_text: e.target.value }))
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tpl_slack">Slack-Nachricht (mrkdwn)</Label>
          <Textarea
            id="tpl_slack"
            rows={6}
            className="font-mono text-xs"
            placeholder={
              "*Konto:* {{account_label}}\n*Grund:* {{reason}}\n*Bounce 24h:* {{bounce_rate_pct}}%"
            }
            value={form.warmup_alert_slack_text}
            onChange={(e) => setForm((p) => ({ ...p, warmup_alert_slack_text: e.target.value }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tpl_telegram">Telegram-Nachricht</Label>
          <Textarea
            id="tpl_telegram"
            rows={6}
            className="font-mono text-xs"
            placeholder={"⚠️ Warmup pausiert\nKonto: {{account_label}}\nGrund: {{reason}}"}
            value={form.warmup_alert_telegram_text}
            onChange={(e) => setForm((p) => ({ ...p, warmup_alert_telegram_text: e.target.value }))}
          />
        </div>

        <div className="flex justify-end">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {save.isPending ? "Speichere…" : "Templates speichern"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
